import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InscriptionEcolePublic() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [form, setForm] = useState({ student_name: "", parent_name: "", parent_email: "", parent_phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ecoles/public/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.sessions?.length === 1) setSessionId(r.data.sessions[0].id);
      })
      .catch(() => setErr("Lien d'inscription invalide ou expiré."));
  }, [token]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!sessionId) { toast.error("Choisis une session"); return; }
    if (!form.student_name.trim()) { toast.error("Le nom de l'élève est requis"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ecoles/public/${token}/register`, { session_id: sessionId, ...form });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Inscription enregistrée ! Merci.");
      setBusy(false);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Inscription impossible");
      setBusy(false);
    }
  };

  if (err) return <Centered><p className="text-red-400 text-lg">{err}</p></Centered>;
  if (!info) return <Centered><p className="text-zinc-400">Chargement…</p></Centered>;

  const sel = info.sessions.find((s) => s.id === sessionId);

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="inscription-ecole-public">
      <div className="max-w-lg mx-auto px-5 py-10">
        <div className="text-center mb-8">
          <div className="text-red-500 text-[11px] tracking-[0.4em] mb-2">INSTITUT IMPACT ÉLITE MARTIAL</div>
          <h1 className="text-3xl font-bold">Inscription parascolaire</h1>
          <p className="text-zinc-400 mt-2">{info.school_name}{info.city ? ` · ${info.city}` : ""}</p>
        </div>

        {info.sessions.length === 0 ? (
          <div className="text-center text-zinc-400 border border-zinc-800 rounded-xl p-8">
            Aucune session ouverte à l'inscription pour le moment. Reviens bientôt !
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-zinc-500 mb-2 block">Choisis la session</label>
              <div className="space-y-2">
                {info.sessions.map((s) => (
                  <button type="button" key={s.id} onClick={() => setSessionId(s.id)} data-testid={`session-opt-${s.id}`}
                    className={`w-full text-left rounded-xl border p-3 transition ${sessionId === s.id ? "border-red-500 bg-red-500/10" : "border-zinc-800 bg-zinc-900 hover:border-zinc-600"}`}>
                    <div className="font-semibold">{s.discipline}</div>
                    <div className="text-sm text-zinc-400">{s.day} · {s.time} · {s.weeks} semaines</div>
                    <div className="flex justify-between mt-1 text-sm">
                      <span className="text-emerald-400">{Number(s.price || 0).toFixed(0)} $</span>
                      <span className={s.spots_left > 0 ? "text-zinc-400" : "text-red-400"}>
                        {s.spots_left > 0 ? `${s.spots_left} places restantes` : "Complet"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <Inp label="Nom de l'élève *" value={form.student_name} onChange={(v) => set("student_name", v)} testid="reg-student" />
            <Inp label="Nom du parent" value={form.parent_name} onChange={(v) => set("parent_name", v)} testid="reg-parent" />
            <Inp label="Courriel du parent" type="email" value={form.parent_email} onChange={(v) => set("parent_email", v)} testid="reg-email" />
            <Inp label="Téléphone" value={form.parent_phone} onChange={(v) => set("parent_phone", v)} testid="reg-phone" />

            <button type="submit" disabled={busy || (sel && sel.spots_left === 0)} data-testid="reg-submit"
              className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 font-semibold py-3 rounded-xl transition">
              {busy ? "Redirection vers le paiement…" : sel ? `S'inscrire et payer ${Number(sel.price || 0).toFixed(0)} $` : "S'inscrire et payer"}
            </button>
            <p className="text-center text-xs text-zinc-600">Paiement sécurisé par Stripe · (819) 527-9446</p>
          </form>
        )}
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs text-zinc-500 mb-1 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-white outline-none focus:border-red-500" />
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-5">{children}</div>;
}
